import { useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { authAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Info, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max 5MB image size');
      return;
    }

    setUploading(true);
    try {
      const res = await authAPI.uploadAvatar(file);
      updateUser({ avatar_url: res.data.avatar_url });
      toast.success('Profile image updated!');
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const avatarSrc = user?.avatar_url ? `${API}${user.avatar_url}` : null;

  return (
    <div className="p-8 space-y-8 max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-2">Manage your account and preferences.</p>
      </div>

      {/* Profile */}
      <Card className="bg-zinc-900/60 border-white/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <User className="w-5 h-5 text-primary" strokeWidth={1.5} /> Profile
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 p-4 rounded-xl bg-secondary/30 border border-white/5">
            {/* Avatar with upload */}
            <div className="relative group shrink-0" data-testid="avatar-container">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleAvatarChange}
                accept="image/*"
                className="hidden"
                data-testid="avatar-file-input"
              />
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={user?.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-white/10"
                  data-testid="avatar-image"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-heading text-xl font-bold" data-testid="avatar-placeholder">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              <button
                onClick={handleAvatarClick}
                disabled={uploading}
                className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                data-testid="avatar-upload-button"
              >
                {uploading
                  ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                  : <Camera className="w-5 h-5 text-white" />
                }
              </button>
            </div>

            <div>
              <p className="font-medium">{user?.name}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> {user?.email}
              </p>
              <button
                onClick={handleAvatarClick}
                className="text-xs text-primary hover:underline mt-1"
                data-testid="change-avatar-link"
              >
                {avatarSrc ? 'Change photo' : 'Add profile photo'}
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* About */}
      <Card className="bg-zinc-900/60 border-white/5">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Info className="w-5 h-5 text-primary" strokeWidth={1.5} /> About
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
            <span className="text-sm text-muted-foreground">Version</span>
            <Badge variant="secondary" className="text-xs">MVP 1.0</Badge>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
            <span className="text-sm text-muted-foreground">AI Model</span>
            <Badge variant="secondary" className="text-xs">GPT-5.2</Badge>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
            <span className="text-sm text-muted-foreground">Transcription</span>
            <Badge variant="secondary" className="text-xs">Whisper</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
